import React, { useState, useEffect } from 'react';
import { 
  Wallet, TrendingUp, RefreshCw, ArrowDownRight, DollarSign,
  PiggyBank, Wrench, Megaphone, Download, History, Coins
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import api from '../../utils/api';

const GameSystemWallets = () => {
  const { toast } = useToast();
  const [wallets, setWallets] = useState(null);
  const [todayRevenue, setTodayRevenue] = useState(null);
  const [circulation, setCirculation] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeWallet, setActiveWallet] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletsRes, txRes] = await Promise.all([
        api.get('/admin/games/system-wallets'),
        api.get('/admin/games/system-wallets-transactions?limit=20')
      ]);

      setWallets(walletsRes.data.wallets);
      setTodayRevenue(walletsRes.data.today_revenue);
      setCirculation(walletsRes.data.total_agp_in_circulation);
      setTransactions(txRes.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch system wallets:', error);
      toast({
        title: "Error",
        description: "Failed to load system wallets",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (walletType) => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast({ title: "Error", description: "Enter valid amount", variant: "destructive" });
      return;
    }

    setWithdrawing(true);
    try {
      const res = await api.post(`/admin/games/system-wallets/${walletType}/withdraw`, {
        amount,
        description: `Admin withdrawal from ${walletType} wallet`
      });

      toast({
        title: "Withdrawal Successful",
        description: `Withdrew ${amount} AGP from ${walletType} wallet`,
      });

      setWithdrawAmount('');
      setActiveWallet(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Withdrawal Failed",
        description: error.response?.data?.detail || "Something went wrong",
        variant: "destructive"
      });
    } finally {
      setWithdrawing(false);
    }
  };

  const getWalletIcon = (type) => {
    switch (type) {
      case 'tax': return <PiggyBank className="w-6 h-6 text-red-400" />;
      case 'admin_profit': return <DollarSign className="w-6 h-6 text-green-400" />;
      case 'maintenance': return <Wrench className="w-6 h-6 text-blue-400" />;
      case 'global_promo': return <Megaphone className="w-6 h-6 text-purple-400" />;
      default: return <Wallet className="w-6 h-6" />;
    }
  };

  const getWalletColor = (type) => {
    switch (type) {
      case 'tax': return 'from-red-500/20 to-red-600/10 border-red-500/30';
      case 'admin_profit': return 'from-green-500/20 to-green-600/10 border-green-500/30';
      case 'maintenance': return 'from-blue-500/20 to-blue-600/10 border-blue-500/30';
      case 'global_promo': return 'from-purple-500/20 to-purple-600/10 border-purple-500/30';
      default: return 'from-gray-500/20 to-gray-600/10';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="game-system-wallets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-7 h-7" />
            Game System Wallets
          </h1>
          <p className="text-muted-foreground text-sm">Game revenue management (Admin Only)</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`bg-gradient-to-br ${getWalletColor('tax')}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {getWalletIcon('tax')}
              <div>
                <p className="text-xs text-muted-foreground">Tax Wallet (30%)</p>
                <p className="text-xl font-bold">{wallets?.tax_wallet?.toFixed(2) || 0} AGP</p>
              </div>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full mt-3"
              onClick={() => setActiveWallet('tax')}
            >
              <Download className="w-3 h-3 mr-1" /> Withdraw
            </Button>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${getWalletColor('admin_profit')}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {getWalletIcon('admin_profit')}
              <div>
                <p className="text-xs text-muted-foreground">Admin Profit (20%)</p>
                <p className="text-xl font-bold">{wallets?.admin_profit_wallet?.toFixed(2) || 0} AGP</p>
              </div>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full mt-3"
              onClick={() => setActiveWallet('admin_profit')}
            >
              <Download className="w-3 h-3 mr-1" /> Withdraw
            </Button>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${getWalletColor('maintenance')}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {getWalletIcon('maintenance')}
              <div>
                <p className="text-xs text-muted-foreground">Maintenance (10%)</p>
                <p className="text-xl font-bold">{wallets?.maintenance_wallet?.toFixed(2) || 0} AGP</p>
              </div>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full mt-3"
              onClick={() => setActiveWallet('maintenance')}
            >
              <Download className="w-3 h-3 mr-1" /> Withdraw
            </Button>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${getWalletColor('global_promo')}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {getWalletIcon('global_promo')}
              <div>
                <p className="text-xs text-muted-foreground">Global Promo (7%)</p>
                <p className="text-xl font-bold">{wallets?.global_promo_wallet?.toFixed(2) || 0} AGP</p>
              </div>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full mt-3"
              onClick={() => setActiveWallet('global_promo')}
            >
              <Download className="w-3 h-3 mr-1" /> Withdraw
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Stats Row */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-500/20 rounded-full">
              <Coins className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Balance</p>
              <p className="text-2xl font-bold">{wallets?.total_balance?.toFixed(2) || 0} AGP</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-full">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Today's Revenue</p>
              <p className="text-2xl font-bold">{todayRevenue?.total?.toFixed(2) || 0} AGP</p>
              <p className="text-xs text-muted-foreground">{todayRevenue?.games_count || 0} games</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-full">
              <Wallet className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">AGP in Circulation</p>
              <p className="text-2xl font-bold">{circulation?.toFixed(2) || 0} AGP</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Split Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Revenue Split (Per Game Entry)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <span className="text-sm">33% Affiliate Network Commission</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm">30% Tax</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm">20% Admin Profit</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-sm">10% Maintenance</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-sm">7% Global Promo</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Recent Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No transactions yet</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {transactions.map((tx, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getWalletIcon(tx.wallet_type)}
                    <div>
                      <p className="font-medium text-sm capitalize">
                        {tx.type?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {tx.wallet_type?.replace(/_/g, ' ')} - {new Date(tx.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount?.toFixed(2)} AGP
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Withdrawal Modal */}
      {activeWallet && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getWalletIcon(activeWallet)}
                Withdraw from {activeWallet.replace(/_/g, ' ')} Wallet
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Available Balance</p>
                <p className="text-2xl font-bold">
                  {activeWallet === 'tax' && wallets?.tax_wallet?.toFixed(2)}
                  {activeWallet === 'admin_profit' && wallets?.admin_profit_wallet?.toFixed(2)}
                  {activeWallet === 'maintenance' && wallets?.maintenance_wallet?.toFixed(2)}
                  {activeWallet === 'global_promo' && wallets?.global_promo_wallet?.toFixed(2)}
                  {' '}AGP
                </p>
              </div>

              <div>
                <label className="text-sm font-medium">Withdraw Amount (AGP)</label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  min="1"
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setActiveWallet(null);
                    setWithdrawAmount('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => handleWithdraw(activeWallet)}
                  disabled={withdrawing || !withdrawAmount}
                >
                  {withdrawing ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Withdraw
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GameSystemWallets;
